import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSite } from '../data/SiteContext';
import { FILTERS, INCLUDED, REGIONS, pic } from '../data/site';
import { fetchHotels, money, stars } from '../data/publicHotels';

/**
 * Region and country are shown as plain text on a destination, but both already have pages.
 * This resolves the display names ("Caribbean", "Dominican Republic") to those routes.
 */
const placeLinks = (dest) => {
  const region = REGIONS.find(r => r.name.toLowerCase() === (dest.region || '').toLowerCase());
  const country = region?.countries.find(c => c.name.toLowerCase() === (dest.country || '').toLowerCase());
  return {
    regionPath: region ? `/regions/${region.key}` : null,
    countryPath: region && country ? `/regions/${region.key}/${country.key}` : null,
  };
};

export function DestinationsList() {
  const navigate = useNavigate();
  const { dests } = useSite();
  return (
    <div className="wrap" style={{ paddingTop:40, paddingBottom:72 }}>
      <div className="kicker">Explore</div>
      <h1 className="page-h1">All destinations</h1>
      <p className="page-sub" style={{ marginBottom:26 }}>From Caribbean beaches to the temples of the Nile — every trip fully tailored and supported.</p>
      {/* Map view */}
      <div style={{ position:'relative', borderRadius:26, overflow:'hidden', marginBottom:30, border:'1px solid #f2e2d2', boxShadow:'0 16px 40px rgba(16,90,90,0.14)' }}>
        <div className="cover"><img src={pic('world,map,vintage', 3, 1600, 800)} alt="World map" className="imgfill" /></div>
        <div style={{ position:'relative', aspectRatio:'2 / 1', minHeight:280 }}>
          {dests.map(d => (
            <div key={d.id} onClick={() => navigate('/destinations/' + d.slug)} style={{ position:'absolute', left:(d.x ?? 50) + '%', top:(d.y ?? 45) + '%', transform:'translate(-50%,-100%)', cursor:'pointer', zIndex:2 }}>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                <div style={{ padding:'6px 12px', borderRadius:100, background:'#ffffff', border:'1px solid #f2e2d2', boxShadow:'0 6px 16px rgba(16,90,90,0.2)', fontSize:12.5, fontWeight:800, color:'#0e8a9c', whiteSpace:'nowrap' }}>{d.name}{d.from ? ` · $${d.from}` : ''}</div>
                <div style={{ width:16, height:16, borderRadius:'50%', marginTop:4, background:'linear-gradient(140deg,#26c6a3,#0e8a9c)', border:'3px solid rgba(255,255,255,0.95)', boxShadow:'0 3px 10px rgba(11,90,83,0.5)' }}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:28 }}>
        {FILTERS.map(f => (
          <div key={f} className="card" style={{ padding:'9px 16px', borderRadius:100, fontWeight:700, fontSize:13.5, color:'#0c2b2f', cursor:'pointer' }}>{f}</div>
        ))}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:22 }}>
        {dests.map(d => (
          <div key={d.id} className="lift" onClick={() => navigate('/destinations/' + d.slug)} style={{ cursor:'pointer', position:'relative', borderRadius:24, overflow:'hidden', minHeight:360, display:'flex', flexDirection:'column', justifyContent:'flex-end', boxShadow:'0 14px 34px rgba(16,90,90,0.14)' }}>
            <div className="cover"><div className="bgimg" style={{ backgroundImage:`url(${d.img})` }}></div></div>
            <div className="cover" style={{ background:'linear-gradient(180deg,rgba(6,40,45,0) 35%,rgba(6,40,45,0.8) 100%)' }}></div>
            <div style={{ position:'absolute', top:14, left:14, padding:'6px 12px', borderRadius:100, background:'rgba(255,255,255,0.25)', border:'1px solid rgba(255,255,255,0.4)', color:'#fff', fontSize:11.5, fontWeight:700 }}>{d.tag}</div>
            <div style={{ position:'relative', padding:20, color:'#fff' }}>
              <div style={{ fontSize:12.5, fontWeight:600, opacity:.9 }}>{d.country}</div>
              <div className="serif" style={{ fontSize:28, margin:'2px 0 8px' }}>{d.name}</div>
              <div style={{ fontSize:14, opacity:.92, lineHeight:1.5, marginBottom:12, display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{d.blurb}</div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span style={{ fontSize:13, opacity:.92 }}>{d.nights} nights · all-inclusive</span>
                <span style={{ fontWeight:800, fontSize:16 }}>{d.from ? `from $${d.from}` : 'price on request'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The hotels curated onto this destination. Loaded on its own rather than with the rest of the
 * site because most visitors never reach a destination page, and prices come from the rate table.
 * Renders nothing at all when there is nothing to sell — an empty heading is worse than no heading.
 */
function WhereYoullStay({ destination }) {
  const [hotels, setHotels] = useState(null);

  useEffect(() => {
    let live = true;
    setHotels(null);
    fetchHotels(destination.id).then(h => { if (live) setHotels(h); }).catch(() => { if (live) setHotels([]); });
    return () => { live = false; };
  }, [destination.id]);

  if (!hotels?.length) return null;

  return (
    <>
      <h3 className="serif" style={{ fontSize:23, margin:'32px 0 6px' }}>Where you'll stay</h3>
      <p style={{ fontSize:14, color:'#5a726f', margin:'0 0 14px' }}>
        {hotels.length} propert{hotels.length === 1 ? 'y' : 'ies'} we book directly in {destination.name}, with live pricing.
      </p>
      <div style={{ display:'grid', gap:10 }}>
        {hotels.slice(0, 5).map(h => (
          <Link key={h.id} to={'/hotels/' + h.id} className="card lift"
            style={{ display:'flex', alignItems:'center', gap:14, padding:10, borderRadius:16, color:'inherit' }}>
            <div style={{ width:78, height:64, borderRadius:12, overflow:'hidden', flexShrink:0 }}>
              <div className="bgimg" style={{ backgroundImage:`url(${h.img})` }}></div>
            </div>
            <div style={{ minWidth:0, flex:1 }}>
              <div style={{ fontWeight:700, fontSize:15, lineHeight:1.25 }}>{h.name}</div>
              <div style={{ fontSize:12.5, color:'#5a726f', marginTop:3 }}>
                {[stars(h.starCategory), h.cityName].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div style={{ textAlign:'right', flexShrink:0, paddingRight:6 }}>
              <div style={{ fontSize:11.5, color:'#5a726f' }}>from</div>
              <div style={{ fontWeight:800, fontSize:16 }}>{money(h.priceFrom)}</div>
            </div>
          </Link>
        ))}
      </div>
      {hotels.length > 5 && (
        <Link to="/hotels" style={{ display:'block', textAlign:'center', marginTop:12, padding:12, borderRadius:13, fontWeight:700, fontSize:14, color:'#0e8a9c', background:'rgba(14,138,122,0.1)' }}>
          See all {hotels.length} hotels →
        </Link>
      )}
    </>
  );
}

export function DestinationDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { dests, loading, contact } = useSite();
  const cur = dests.find(d => d.slug === id) || dests.find(d => String(d.id) === id) || dests[0];
  if (!cur) {
    return (
      <div className="wrap" style={{ paddingTop:80, paddingBottom:120, textAlign:'center', color:'#5a726f' }}>
        {loading ? 'Loading…' : 'Destination not found.'}
      </div>
    );
  }
  const { regionPath, countryPath } = placeLinks(cur);
  return (
    <div>
      <section style={{ position:'relative', minHeight:'clamp(420px,60vh,560px)', display:'flex', alignItems:'flex-end', overflow:'hidden' }}>
        <div className="cover"><div className="bgimg" style={{ backgroundImage:`url(${cur.img})` }}></div></div>
        <div className="cover" style={{ background:'linear-gradient(180deg,rgba(6,40,45,0.15),rgba(6,40,45,0.72))' }}></div>
        <div style={{ position:'relative', maxWidth:1240, margin:'0 auto', padding:'0 24px 44px', width:'100%', color:'#fff' }}>
          <span onClick={() => navigate('/destinations')} style={{ cursor:'pointer', color:'rgba(255,255,255,0.9)', fontWeight:700, fontSize:14 }}>← All destinations</span>
          <div style={{ display:'inline-block', margin:'14px 0 8px', padding:'6px 14px', borderRadius:100, background:'rgba(255,255,255,0.2)', border:'1px solid rgba(255,255,255,0.4)', fontSize:12.5, fontWeight:700, marginLeft:12 }}>{cur.tag}</div>
          <h1 className="serif" style={{ fontSize:'clamp(38px,6vw,66px)', margin:0, letterSpacing:'-0.5px' }}>{cur.name}</h1>
          {/* Country and region both have their own pages — say so rather than printing dead text. */}
          <div style={{ fontSize:14.5, marginTop:8, opacity:.95 }}>
            {countryPath
              ? <Link to={countryPath} style={{ color:'#fff', fontWeight:700, borderBottom:'1px solid rgba(255,255,255,0.5)' }}>{cur.country}</Link>
              : <span style={{ fontWeight:700 }}>{cur.country}</span>}
            {cur.region && <>
              <span style={{ opacity:.6, margin:'0 8px' }}>·</span>
              {regionPath
                ? <Link to={regionPath} style={{ color:'#fff', fontWeight:700, borderBottom:'1px solid rgba(255,255,255,0.5)' }}>{cur.region}</Link>
                : <span style={{ fontWeight:700 }}>{cur.region}</span>}
            </>}
          </div>
        </div>
      </section>
      <div style={{ maxWidth:1240, margin:'0 auto', padding:'44px 24px 72px', display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:40 }}>
        <div>
          <h2 className="serif" style={{ fontSize:30, margin:'0 0 12px' }}>Overview</h2>
          <p style={{ fontSize:16.5, lineHeight:1.7, color:'#0c2b2f' }}>{cur.blurb} Our local team handles flights, transfers, resort selection and every excursion, so all you have to do is show up and unwind.</p>
          {cur.from > 0 && <h3 className="serif" style={{ fontSize:23, margin:'28px 0 14px' }}>What's included</h3>}
          {cur.from > 0 && <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:12 }}>
            {INCLUDED.map(i => (
              <div key={i} className="card" style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 16px', borderRadius:14, fontWeight:600, fontSize:14 }}>
                <span style={{ width:22, height:22, borderRadius:'50%', background:'rgba(14,138,122,0.15)', color:'#0e8a9c', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:13, flexShrink:0 }}>✓</span>{i}
              </div>
            ))}
          </div>}
          <WhereYoullStay destination={cur} />

          <h3 className="serif" style={{ fontSize:23, margin:'32px 0 14px' }}>Gallery</h3>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12 }}>
            {cur.galleryUrls?.length > 0 ? cur.galleryUrls.map((u, i) => (
              <div key={u} style={{ height:150, borderRadius:16, overflow:'hidden' }}><img src={u} alt={`${cur.name} ${i + 1}`} className="imgfill" /></div>
            )) : (
              <>
                <div style={{ height:150, borderRadius:16, overflow:'hidden' }}><img src={pic('resort,pool,tropical', 71, 900, 700)} alt="Resort pool" className="imgfill" /></div>
                <div style={{ height:150, borderRadius:16, overflow:'hidden' }}><img src={pic('beach,golden,hour', 72, 900, 700)} alt="Beach at golden hour" className="imgfill" /></div>
                <div style={{ height:150, borderRadius:16, overflow:'hidden' }}><img src={pic('cocktail,tropical,food', 73, 900, 700)} alt="Local food and drinks" className="imgfill" /></div>
              </>
            )}
          </div>
        </div>
        <div>
          <div className="card" style={{ position:'sticky', top:100, padding:26, borderRadius:24, boxShadow:'0 16px 40px rgba(16,90,90,0.14)' }}>
            {/* An unpriced destination is quoted per trip — show that honestly instead of "$0". */}
            {cur.from ? (
              <>
                <div style={{ fontSize:13, color:'#5a726f', fontWeight:600 }}>Packages from</div>
                <div className="serif" style={{ fontSize:44, color:'#0e8a9c', lineHeight:1, margin:'2px 0 2px' }}>${cur.from}</div>
                <div style={{ fontSize:13, color:'#5a726f', marginBottom:18 }}>per person · {cur.nights} nights all-inclusive</div>
              </>
            ) : (
              <>
                <div className="serif" style={{ fontSize:26, color:'#0e8a9c', lineHeight:1.2, margin:'2px 0 6px' }}>Price on request</div>
                <div style={{ fontSize:13, color:'#5a726f', marginBottom:18 }}>Every trip here is quoted individually — tell us your dates and we'll price it.</div>
              </>
            )}
            {cur.hotelPriceFrom != null && (
              <div style={{ fontSize:13, color:'#5a726f', margin:'-8px 0 18px', paddingBottom:14, borderBottom:'1px solid rgba(14,138,122,0.12)' }}>
                Room only, if you'd rather book just the hotel: from <b style={{ color:'#0c2b2f' }}>{money(cur.hotelPriceFrom)}</b>.
              </div>
            )}
            <a href={contact.waLink} target="_blank" rel="noreferrer" className="btn btn-green" style={{ display:'block', marginTop:18, padding:15, boxShadow:'0 10px 22px rgba(18,153,76,0.35)' }}>Request this trip on WhatsApp</a>
            <a href={contact.telLink} style={{ display:'block', textAlign:'center', marginTop:10, padding:13, borderRadius:14, fontWeight:700, color:'#0e8a9c', background:'rgba(14,138,122,0.1)' }}>Or call {contact.phone}</a>
          </div>
        </div>
      </div>
    </div>
  );
}
